import { TheoryApiAxiosBase } from '@/repository/base/axiosBase';
import { Util } from '@/repository/repositories/utils';
import {
  CreateAccountParam,
  AccountBasicResponse,
  AccountDetailResponse,
  FindOneAccountQuery,
  UpdateAccountBasicParam,
  UpdateAccountBasicQuery,
  AccountTotalFootprintsResponse,
  AccountAllParticipationProjectsResponse,
  AccountParticipationOrganizationsResponse,
  FindAccountFootprintsQuery,
  AccountFootprintsResponse,
  UpdateAccountSelfIntroductionParam,
  UpdateAccountRetentionSkillParam,
  UpdateAccountRetentionQualificationParam,
  UpdateAccountParticipationConditionParam,
  UpdateAccountParticipationConditionQuery,
  UpdateAccountImageQuery,
  FindAccountParticipationProjectsQuery,
  AccountBasicParticipationProjectsResponse
} from '@/@types/Account';

export class AccountsRepository {
  private readonly requestBasePath = 'v1/accounts';
  private readonly axios = new TheoryApiAxiosBase();
  private readonly util = new Util();
  /** アカウント登録 */
  async createAccount(param: CreateAccountParam) {
    return await this.axios.post(this.requestBasePath, param);
  }

  /** アカウント詳細取得(detail-flag=true) */
  async findOneAccountDetail (accountId: number) {
    const path = `${this.requestBasePath}/${accountId}`
    const response = await this.axios.get<AccountDetailResponse>(path, { params: { 'detail-flag': true } });
    return response.data;
  }

  /** アカウント詳細取得(detail-flag=false) */
  async findOneAccountBasic (
    accountId: number,
    query: FindOneAccountQuery,
  ) {
    const endPoint = this.endPointCreator(
      accountId,
    );
    return (await this.axios.get<AccountBasicResponse>(
      endPoint,
      { params: query },
    )).data;
  }

  /** アカウント基本情報更新 */
  async updateAccountBasic (
    accountId: number,
    param: UpdateAccountBasicParam,
    query: UpdateAccountBasicQuery,
  ) {
    const endPoint = this.endPointCreator(
      accountId, 'basic',
    );
    await this.axios.patch(endPoint, param, { params: query });
  }

  /** アカウント削除 */
  async deleteAccount (accountId: number) {
    const endPoint = this.endPointCreator(
      accountId,
    );
    await this.axios.delete(endPoint);
  }

  /** アカウント足跡件数取得 */
  async getAccountTotalFootprints (accountId: number) {
    const endPoint = this.endPointCreator(
      accountId,
      'footprints-number',
    );
    return (await this.axios.get<AccountTotalFootprintsResponse>(
      endPoint,
    )).data;
  }

  /** アカウント参画プロジェクト一覧取得 allモード */
  async findAllAccountParticipationProjects (
    accountId: number,
  ) {
    const endPoint = this.endPointCreator(
      accountId,
      'participated-projects',
    );
    return (await this.axios.get<AccountAllParticipationProjectsResponse>(
      endPoint,
      { params: { 'get-mode': 'all' } },
    )).data;
  }

  /** アカウント参画プロジェクト一覧取得 basicモード */
  async findBasicAccountParticipationProjects (
    accountId: number,
    organizationId: number,
  ) {
    const endPoint = this.endPointCreator(
      accountId,
      'participated-projects',
    );
    return (await this.axios.get<AccountBasicParticipationProjectsResponse>(
      endPoint,
      { params:
        {
          'get-mode': 'basic',
          'organization-id': organizationId,
        }
      },
    )).data;
  }

  /** アカウント参画組織一覧取得 */
  async findAllAccountParticipationOrganizations (
    accountId: number,
  ) {
    const endPoint = this.endPointCreator(
      accountId,
      'participated-organizations',
    );
    return (await this.axios.get<AccountParticipationOrganizationsResponse>(
      endPoint,
    )).data;
  }

  /** アカウント足跡一覧取得 */
  async findAccountFootprints (
    accountId: number,
    query: FindAccountFootprintsQuery,
  ) {
    const endPoint = this.endPointCreator(
      accountId,
      'footprints',
    );
    return (await this.axios.get<AccountFootprintsResponse>(
      endPoint,
      { params: query },
    )).data;
  }

  /** アカウントスキル一括更新 */
  async updateAccountAllSkill (
    accountId: number,
    param: UpdateAccountRetentionSkillParam,
  ) {
    const endPoint = this.endPointCreator(
      accountId,
      'all-skills',
    );
    await this.axios.patch(endPoint, param);
  }

  /** アカウント資格一括更新 */
  async updateAccountAllQualification (
    accountId: number,
    param: UpdateAccountRetentionQualificationParam,
  ) {
    const endPoint = this.endPointCreator(
      accountId,
      'all-qualifications',
    );
    await this.axios.patch(endPoint, param);
  }

  /** アカウント自己紹介更新 */
  async updateAccountSelfIntroduction (
    accountId: number,
    param: UpdateAccountSelfIntroductionParam,
  ) {
    const endPoint = this.endPointCreator(
      accountId,
      'self-introduction',
    );
    await this.axios.patch(endPoint, param);
  }

  /** アカウント希望参画条件更新 */
  async updateAccountParticipationCondition (
    accountId: number,
    param: UpdateAccountParticipationConditionParam,
    query: UpdateAccountParticipationConditionQuery,
  ) {
    const endPoint = this.endPointCreator(
      accountId,
      'participation-condition',
    );
    await this.axios.patch(endPoint, param, { params: query });
  }

  /** アカウント画像ファイル更新 */
  async updateAccountImage(
    accountId: number,
    param: FormData,
    query: UpdateAccountImageQuery
  ) {
    const endPoint = this.endPointCreator(
      accountId,
      'image',
    );
    await this.axios.patch(endPoint, param, {
      params: query,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
    });
  }

  /** アカウントスキルシート更新 */
  async updateAccountSkillsheet(
    accountId: number,
    param: FormData,
  ) {
    const endPoint = this.endPointCreator(
      accountId,
      'skillsheet',
    );
    await this.axios.patch(endPoint, param, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
    });
  }

  private endPointCreator(...paths: (string | number)[]) {
    return this.util.endPointCreator(this.requestBasePath, ...paths);
  }
}
